import { addMonths, format, isValid, parseISO } from 'date-fns';

import {
  QUIZ_GROUP_MIN_TERM,
  QUIZ_GROUP_MIN_USERS,
  QUIZ_GROUP_PRO_UNIT_PRICE,
  QUIZ_QUOTE_UNIT_PRICE,
} from '../constants/quizQuote.const';

import { type QuizQuoteFormValues } from './quizQuote.schema';
import {
  type QuizQuoteApiPlan,
  type QuizQuoteApiPlanType,
} from './quizQuoteApi';

export interface QuizQuotePricing {
  amount: number;
  regularAmount: number;
  totalAmount: number;
  discountAmount: number;
  earlybirdAmount: number;
  finalAmount: number;
  hasGroupDiscount: boolean;
  isGroupAutoApplied: boolean;
}

export interface QuizQuoteDisplayData extends QuizQuotePricing {
  schoolName: string;
  accountName: string;
  accountPhone: string;
  accountEmail: string;
  etc?: string;
  plan: QuizQuoteApiPlan;
  planType: QuizQuoteApiPlanType;
  userCount: number;
  term: number;
  startDate: string;
  endDate: string;
}

function getEndDate(startDate: string, term: number): string {
  const date = parseISO(startDate);
  if (!isValid(date) || !Number.isFinite(term) || term < 1) return startDate;

  return format(addMonths(date, Math.floor(term)), 'yyyy-MM-dd');
}

export function calculateQuotePricing(
  plan: QuizQuoteApiPlan,
  planType: QuizQuoteApiPlanType,
  userCount: number,
  term: number,
): QuizQuotePricing {
  if (plan === 'basic' && planType === 'group') {
    throw new Error('Basic 플랜은 학교·단체 견적을 지원하지 않습니다.');
  }

  const regularAmount = QUIZ_QUOTE_UNIT_PRICE[plan];
  // 개인으로 골랐지만 그룹 조건(5인·3개월)을 충족하면 자동으로 그룹 단가를 적용한다.
  const isGroupAutoApplied =
    plan === 'pro' &&
    planType === 'individual' &&
    userCount >= QUIZ_GROUP_MIN_USERS &&
    term >= QUIZ_GROUP_MIN_TERM;
  const hasGroupDiscount =
    (plan === 'pro' && planType === 'group') || isGroupAutoApplied;
  const amount = hasGroupDiscount ? QUIZ_GROUP_PRO_UNIT_PRICE : regularAmount;
  const totalAmount = regularAmount * userCount * term;
  const finalAmount = amount * userCount * term;

  return {
    amount,
    regularAmount,
    totalAmount,
    discountAmount: totalAmount - finalAmount,
    earlybirdAmount: 0,
    finalAmount,
    hasGroupDiscount,
    isGroupAutoApplied,
  };
}

export function toQuizQuoteDisplayData(
  values: QuizQuoteFormValues,
): QuizQuoteDisplayData {
  const pricing = calculateQuotePricing(
    values.plan,
    values.plan_type,
    values.user_count,
    values.term,
  );

  return {
    schoolName: values.school_name,
    accountName: values.account_name,
    accountPhone: values.account_phone,
    accountEmail: values.account_email,
    etc: values.annotation || undefined,
    plan: values.plan,
    planType: values.plan_type,
    userCount: values.user_count,
    term: values.term,
    startDate: values.start_date,
    endDate: getEndDate(values.start_date, values.term),
    ...pricing,
  };
}
